import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import DeleteModal from "@/components/app/DeleteModal";
import { useRoleController } from "@/components/useRoleController";
import { PROVIDER_PATHS } from "@/constants/paths";
import useDisclosure from "@/hooks/use-disclosure";
import { useArchiveState } from "@/hooks/useArchiveState";
import { SignedNoteSummary } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-preview";
import { useAppointmentsV2 } from "@/queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { useArchiveAppointment } from "@/queries/use-archieve-appointment";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  filterActionCol,
  getXtenantId,
} from "@/utils/utils";
import CreateVisitNoteModal from "./CreateVisitNoteModal";
import { useVisitHistoryColumns } from "./hooks/use-visit-history-columns";
import {
  DEFAULT_VISIT_NOTE_TYPE,
  VISIT_HISTORY_LABELS,
} from "./visit-detail-constant";

const PatientVisitHistory = () => {
  const { patientId } = useParams() as { patientId: string };
  const navigate = useNavigate();

  const { mutateAsync: archiveAppointment } = useArchiveAppointment();
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const createVisitNoteDisclosure = useDisclosure();
  const encounterSummeryDisc = useDisclosure();
  const {
    data: visitDetails,
    refetch,
    isLoading: isLoadingVisitDetails,
  } = useAppointmentsV2({
    providerGroupId: getXtenantId(),
    gridSorting,
    pagination,
    appStatusForPatient: ["CONFIRMED"],
    patientUuid: [patientId],
  });

  const { deleteDisc, archiveHandler } = useArchiveState(archiveAppointment);
  const { hasReadOnly, hasUpdate, hasNone } = useRoleController("Visit");
  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  const openVisit = useCallback(
    (appointmentId: string) => encounterSummeryDisc.open(appointmentId),
    [encounterSummeryDisc]
  );
  const completeVisit = useCallback(
    (appointmentId: string) =>
      navigate(
        `${PROVIDER_PATHS.NEW_PATIENT}/${patientId}/visit-note/form?appointmentid=${appointmentId}&noteType=${DEFAULT_VISIT_NOTE_TYPE}`
      ),
    [navigate, patientId]
  );
  const deleteVisit = useCallback(
    (appointmentId: string) => deleteDisc.open(appointmentId),
    [deleteDisc]
  );

  const columns = useVisitHistoryColumns({
    showLastUpdated: !isVisitNotePage,
    hasUpdate,
    hasReadOnly,
    onView: openVisit,
    onEdit: completeVisit,
    onDelete: deleteVisit,
  });

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {encounterSummeryDisc.opened && encounterSummeryDisc.data && (
        <SignedNoteSummary
          key={encounterSummeryDisc.data}
          disclosure={encounterSummeryDisc}
        />
      )}

      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(true, id).then(() => {
            refetch();
          })
        }
        content={VISIT_HISTORY_LABELS.deleteConfirmation}
      />
      {createVisitNoteDisclosure.opened && (
        <CreateVisitNoteModal disclosureHost={createVisitNoteDisclosure} />
      )}

      <CustomDataGridV2
        loading={isLoadingVisitDetails}
        getRowId={(row) => row?.uuid}
        rows={visitDetails?.data?.content ?? []}
        rowCount={visitDetails?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(columns, hasNone)}
        paginationModel={pagination}
        sortModel={gridSorting}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
      />
    </Box>
  );
};

export default PatientVisitHistory;
